/**
 * Plan Module Resolution
 *
 * Resolves the plan module ids that unlock features for a non-admin tenant.
 * Mirrors the legacy frontend: fetch the plan catalogue, match the user's
 * plan_id, read its `modules` array (stored JSON, sometimes a string).
 * Best effort — a failure just yields no extra modules (role gating still
 * applies), so callers never break on it.
 *
 * Shared by the login flow (authProvider) and by starting an impersonation
 * session (RowActionsMenu): the effective plan-module gating must follow
 * whichever user is currently acting, exactly like the effective role does.
 */

import { httpClient } from '@/utils/httpClient';
import { ROLE_IDS } from '@/config/constants';
import type { ApiEnvelope, ApiListData, ApiUser } from '@/types/api';

export const resolvePlanModules = async (
  user: Pick<ApiUser, 'role_id' | 'plan_id'>
): Promise<number[]> => {
  if (user.role_id === ROLE_IDS.ADMIN || user.plan_id == null) return [];
  try {
    const response = await httpClient.get<ApiEnvelope<ApiListData | Record<string, any>[]>>(
      '/plans'
    );
    const payload = response.data?.data as any;
    const rows: Record<string, any>[] = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.rows)
      ? payload.rows
      : [];
    const plan = rows.find((p) => Number(p.plan_id) === Number(user.plan_id));
    if (!plan) return [];
    const raw = plan.modules;
    const modules = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(modules)
      ? modules.map((m: unknown) => Number(m)).filter((m: number) => !Number.isNaN(m))
      : [];
  } catch {
    return [];
  }
};

export default resolvePlanModules;
